import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/** A brand's Google Analytics, and the visits AI assistants sent it. */

type BrandPath = paths["/api/workspaces/{workspaceId}/brands/{brandId}/analytics"];
type VisitsPath = paths["/api/workspaces/{workspaceId}/reports/{reportId}/ai-visits"];
type PropertiesPath =
  paths["/api/workspaces/{workspaceId}/brands/{brandId}/analytics/properties"];

export type AnalyticsConnection =
  BrandPath["get"]["responses"][200]["content"]["application/json"];
export type AnalyticsProperty =
  PropertiesPath["get"]["responses"][200]["content"]["application/json"]["properties"][number];
export type AiVisits = VisitsPath["get"]["responses"][200]["content"]["application/json"];
export type AiVisitsRead = Extract<AiVisits, { state: "read" }>;
export type AiAssistantKey = AiVisitsRead["assistants"][number]["assistant"];

const onBrand = (workspaceId: string, brandId: string) => ({
  path: { workspaceId, brandId },
});

/**
 * Where the Connect button sends the browser: a full navigation, not a fetch,
 * because the API answers with a redirect to Google's consent screen.
 */
export function analyticsConnectUrl(workspaceId: string, brandId: string): string {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/brands/${encodeURIComponent(brandId)}/analytics/connect`;
}

export async function fetchAnalyticsConnection(
  workspaceId: string,
  brandId: string,
): Promise<AnalyticsConnection> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/brands/{brandId}/analytics",
    { params: onBrand(workspaceId, brandId) },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "load the Google Analytics connection"));
  return data;
}

export async function fetchAnalyticsProperties(
  workspaceId: string,
  brandId: string,
): Promise<readonly AnalyticsProperty[]> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/brands/{brandId}/analytics/properties",
    { params: onBrand(workspaceId, brandId) },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "list your Google Analytics properties"));
  return data.properties;
}

export async function chooseAnalyticsProperty(
  workspaceId: string,
  brandId: string,
  propertyId: string,
): Promise<AnalyticsConnection> {
  const { data, response } = await api.PUT(
    "/api/workspaces/{workspaceId}/brands/{brandId}/analytics/property",
    { params: onBrand(workspaceId, brandId), body: { propertyId } },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "use that Google Analytics property"));
  return data;
}

export async function disconnectAnalytics(
  workspaceId: string,
  brandId: string,
): Promise<{ readonly revokedAtGoogle: boolean }> {
  const { data, response } = await api.DELETE(
    "/api/workspaces/{workspaceId}/brands/{brandId}/analytics",
    { params: onBrand(workspaceId, brandId) },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "disconnect Google Analytics"));
  return data;
}

export async function fetchAiVisits(
  workspaceId: string,
  reportId: string,
  range: { readonly from: string; readonly to: string },
): Promise<AiVisits> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/ai-visits",
    { params: { path: { workspaceId, reportId }, query: range } },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "load the visits AI sent you"));
  return data;
}
